import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";  // Importera layout-komponenten
import Login from "./components/Login";
import NoLicense from "./components/NoLicense";
import ErrorBoundary from "./components/ErrorBoundary";
import OnboardingWizard, { hasCompletedOnboarding } from "./components/OnboardingWizard";
import { ToastProvider } from "./contexts/ToastContext";
import sessionService from "./services/SessionService";
import settingsService from "./services/SettingsService";
import storage from "./services/StorageAdapter";
import { initStorage, CRITICAL_KEYS } from "./services/storageInit";
import { t, initTranslations } from "./useTranslations";
import useAccessibleModules from "./hooks/useAccessibleModules";

// Shared component CSS imported here so webpack bundles them in the main chunk
// with a deterministic order, avoiding mini-css-extract-plugin ordering warnings
// when these styles are also referenced from lazy-loaded module chunks.
import "./components/SkeletonLoader.css";
import "./components/PageHeader.css";
import "./components/UnifiedModal.css";
import "./components/EmptyState.css";
import "./components/UnifiedCard.css";
import "./styles/HelpTooltip.css";
import "./styles/DataValidation.css";

// Lazy-loaded modules — each module is only fetched when the user navigates to it
const Dashboard = lazy(() => import("./modules/Dashboard/Dashboard"));
const CustomerRisk = lazy(() => import("./modules/CustomerRisk/CustomerRisk"));
const GeoRisk = lazy(() => import("./modules/GeoRisk/GeoRisk"));
const Admin = lazy(() => import("./modules/Admin/Admin"));
const BWRA = lazy(() => import("./modules/BWRA/BWRA"));
const TransactionMonitoring = lazy(() => import("./modules/TransactionMonitoring/TransactionMonitoring"));
const PeriodicReporting = lazy(() => import("./modules/PeriodicReporting/PeriodicReporting"));

// Select backend (Supabase or localStorage) based on env vars — must run
// before any service reads so that all subsequent calls use the right backend.
initStorage();

const applyTheme = (theme) => {
  if (theme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
};

const MODULE_COMPONENTS = {
  'dashboard': Dashboard,
  'customer-risk': CustomerRisk,
  'geographical-risk': GeoRisk,
  'bwra': BWRA,
  'transaction-monitoring': TransactionMonitoring,
  'periodic-reporting': PeriodicReporting,
  'admin': Admin
};

// Inner component that can use hooks (must be inside Router context for useNavigate)
const AppRoutes = ({ isAuth, onLogin, onLogout }) => {
  const [isSidebarExpanded, setSidebarExpanded] = useState(false);
  const accessibleIds = useAccessibleModules();
  const allModules = settingsService.getEnabledModules();
  // Modules accessible to the current user and licensed for the active entity
  const enabledModules = allModules.filter(m => accessibleIds.includes(m.id));

  const defaultRoute = enabledModules.length > 0 ? enabledModules[0].route : '/dashboard';

  const moduleFallback = (
    <div className="app-hydrating">
      <span className="app-hydrating-spinner" />
      <span className="app-hydrating-text">{t('app.loading')}</span>
    </div>
  );

  if (!isAuth) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={onLogin} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Suspense fallback={moduleFallback}>
      <Routes>
        <Route path="/login" element={<Navigate to={defaultRoute} replace />} />
        <Route element={<Layout isSidebarExpanded={isSidebarExpanded} toggleSidebar={() => setSidebarExpanded(!isSidebarExpanded)} onLogout={onLogout} />}>
          <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          {enabledModules.map(mod => {
            const Component = MODULE_COMPONENTS[mod.id];
            if (!Component) return null;
            return <Route key={mod.id} path={mod.route} element={<Component />} />;
          })}
          {/* Fallback: show NoLicense for any module route that exists in settings but isn't accessible */}
          {allModules.filter(m => !accessibleIds.includes(m.id)).map(mod => (
            <Route key={`nolic-${mod.id}`} path={mod.route} element={<NoLicense />} />
          ))}
          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

const App = () => {
  // Start as false — auth state is determined after hydration completes.
  // This prevents a flash of unauthenticated UI while Supabase data loads.
  const [isAuth, setIsAuth] = useState(false);
  const [isHydrating, setIsHydrating] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleLogin = useCallback(() => {
    setIsAuth(true);
    // Notify useAccessibleModules to recalculate now that a session exists
    window.dispatchEvent(new Event('rms_licenses_changed'));
    if (!hasCompletedOnboarding()) {
      setShowOnboarding(true);
    }
  }, []);

  const handleLogout = useCallback(() => {
    storage.clearCache();
    setIsAuth(false);
  }, []);

  useEffect(() => {
    // Async init: hydrate critical keys, refresh session, apply theme.
    // Hydration must complete before the first render so that sync cache
    // reads in services return fresh data (required when SupabaseBackend is
    // active, since its sync getItem() always returns null).
    const init = async () => {
      await storage.hydrate(CRITICAL_KEYS);
      // Hydrate ALL remaining keys from the backend (entity-specific data:
      // data sources, field mappings, model config, indexes, etc.).
      // Required for SupabaseBackend where sync getItem() returns null —
      // without this, entity data appears empty after login.
      await storage.hydrateAll();
      // Migrate legacy session format (localStorage only — no-op for Supabase)
      sessionService.migrateOldSession();
      await sessionService.refreshSession();
      // Determine auth state from hydrated cache
      setIsAuth(sessionService.isValidSession());
      setIsHydrating(false);
      // Apply saved theme from hydrated cache
      const settings = settingsService.getSettings();
      applyTheme(settings.ui?.theme);
      // Hydrate translations in background (fire-and-forget)
      initTranslations();
    };
    init();

    // Event listeners set up synchronously — do not wait for hydration
    const handleThemeChange = (e) => applyTheme(e.detail.theme);
    window.addEventListener('rms_theme_changed', handleThemeChange);

    const handleSessionExpired = () => setIsAuth(false);
    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('rms_theme_changed', handleThemeChange);
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  if (isHydrating) {
    return (
      <div className="app-hydrating">
        <span className="app-hydrating-spinner" />
        <span className="app-hydrating-text">{t('app.loading')}</span>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AppRoutes isAuth={isAuth} onLogin={handleLogin} onLogout={handleLogout} />
          {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
